<template>
  <button
    :class="getClasses"
    class="btn main-white-text flex-center w-100"
    :type="type"
    :disabled="disabled"
    :role="role"
    :style="{ maxWidth: width, height }"
    @click="buttonClick"
  >
    <slot></slot>
  </button>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import type { PropType } from 'vue'
  import { ButtonColorsI, ButtonSizeI, ButtonTypesI } from '@/typescript/types/ui-components/ButtonItem'

  export default defineComponent({
    name: 'ButtonItem',
    props: {
      buttonColor: {
        type: String as PropType<ButtonColorsI>,
        default: 'default',
        validate(val: string) {
          return [
            'aquamarin-outline',
            'aquamarin',
            'white',
            'light-turquoise',
            'orange',
            'blue',
            'green-aqua',
            'light-green',
            'red-btn',
            'dark-red',
            'transparent-btn',
          ].includes(val)
        },
      },
      buttonSize: {
        type: String as PropType<ButtonSizeI>,
        default: 'default',
        validate(val: string) {
          return ['large', 'big', 'small', 'middle', 'wide', 'content'].includes(val)
        },
      },
      type: {
        type: String as PropType<ButtonTypesI>,
        default: 'button',
        validate(val: string) {
          return ['submit', 'reset', 'button'].includes(val)
        },
      },
      width: { type: String, default: null },
      height: { type: String, default: null },
      disabled: { type: Boolean, default: false },
      role: { type: String, default: 'button' },
    },
    emits: ['buttonClick'],
    computed: {
      getButtonColor() {
        switch (this.buttonColor) {
          case 'aquamarin-outline':
            return 'aquamarin-btn-outline'
          case 'aquamarin':
            return 'aquamarin-btn'
          case 'white':
            return 'white-btn'
          case 'light-turquoise':
            return 'light-turquoise-btn'
          case 'orange':
            return 'orange-btn'
          case 'blue':
            return 'blue-btn'
          case 'green-aqua':
            return 'green-aqua-btn'
          case 'light-green':
            return 'light-green-btn'
          case 'red-btn':
            return 'red-btn'
          case 'dark-red':
            return 'dark-red-btn'
          case 'transparent-btn':
            return 'transparent-btn'
          default:
            return 'orange-btn'
        }
      },
      getButtonSize() {
        switch (this.buttonSize) {
          case 'large':
            return 'large-btn'
          case 'big':
            return 'big-btn'
          case 'small':
            return 'small-btn'
          case 'middle':
            return 'middle-btn'
          case 'wide':
            return 'wide'
          case 'content':
            return 'content-btn'
          default:
            return 'wide'
        }
      },
      getClasses() {
        return `${this.getButtonColor} ${this.getButtonSize}`
      },
    },
    methods: {
      buttonClick() {
        if (!this.disabled) this.$emit('buttonClick')
      },
    },
  })
</script>

<style scoped lang="scss">
  .btn {
    display: inline-block;
    padding: 7px 10px;
    border-radius: 7px;
    font-size: 14px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s ease;
    white-space: nowrap;

    &:focus-visible {
      outline: 2px solid var(--color-orange-focus);
      outline-offset: 2px;
    }

    &.large-btn {
      height: 80px;
      font-size: 24px;
    }
    &.big-btn {
      padding: 10px 16px;
      font-size: 14px;
    }
    &.small-btn {
      padding: 5px;
      height: 35px;
      max-width: 110px;
      font-size: 12px;
    }
    &.middle-btn {
      height: 35px;
      max-width: 200px;
      font-size: 12px;
    }
    &.wide {
      height: 40px;
      width: 100%;
      padding: 8px 12px;
      font-size: 12px;
      font-weight: 500;
    }
    &.content-btn {
      width: auto;
      padding: 8px 12px;
      font-size: 12px;
      box-sizing: border-box;
    }

    /* aquamarin-outline */
    &.aquamarin-btn-outline {
      background-color: transparent;
      border: 1px solid var(--color-btn-aquamarin-otline);
      color: var(--color-btn-aquamarin-otline);

      &:hover {
        background-color: var(--color-btn-aquamarin-otline-hover);
      }
      &:disabled {
        background-color: var(--color-btn-disabled-bg);
        color: var(--color-btn-disabled-text);
        border-color: transparent;
        cursor: not-allowed;
      }
    }

    /* aquamarin (filled) */
    &.aquamarin-btn {
      background-color: var(--color-btn-aquamarin);
      border: 2px solid var(--color-btn-aquamarin);
      color: var(--color-btn-aquamarin-text);

      &.dark {
        &:hover {
          color: var(--color-btn-aquamarin);
        }
        &:disabled {
          background-color: var(--color-btn-disabled-bg);
          color: var(--color-btn-disabled-text);
          border-color: transparent;
          cursor: not-allowed;
        }
      }

      &:hover {
        background-color: var(--color-btn-aquamarin-hover);
        color: var(--color-btn-aquamarin-outline);
        border: 2px solid var(--color-btn-aquamarin-outline);
      }

      &:disabled {
        background-color: var(--color-btn-disabled-bg);
        color: var(--color-btn-disabled-text);
        border-color: transparent;
        cursor: not-allowed;
      }
    }

    /* white */
    &.white-btn {
      background-color: var(--color-white);
      border: 1px solid var(--color-text-grey);
      color: var(--color-black);

      &:hover {
        background-color: var(--color-light-grey);
      }
      &:disabled {
        background-color: var(--color-btn-disabled-bg);
        color: var(--color-btn-disabled-text);
        border-color: transparent;
        cursor: not-allowed;
      }
    }

    /* light-turquoise */
    &.light-turquoise-btn {
      background-color: var(--color-btn-turquoise);
      border: 1px solid var(--color-btn-turquoise);
      color: var(--color-btn-turquoise-text);

      &:hover {
        background-color: var(--color-btn-turquoise-hover);
        color: var(--color-btn-turquoise-outline);
      }
      &:disabled {
        background-color: var(--color-btn-disabled-bg);
        color: var(--color-btn-disabled-text);
        border-color: transparent;
        cursor: not-allowed;
      }
    }

    /* ORANGE (primary) */
    &.orange-btn {
      background-color: var(--color-btn-orange);
      border: 1px solid var(--color-btn-orange);
      color: var(--color-white);

      &.non-hovered:hover {
        background-color: var(--color-btn-orange);
        border: 1px solid var(--color-btn-orange);
        color: var(--color-white);
      }

      &:hover {
        background-color: var(--color-btn-orange-hover);
        border-color: var(--color-btn-orange-hover);
      }

      &:disabled {
        background-color: var(--color-btn-disabled-bg);
        color: var(--color-btn-disabled-text);
        border-color: transparent;
        cursor: not-allowed;
      }
    }

    /* blue */
    &.blue-btn {
      background-color: var(--color-btn-blue);
      border: 2px solid var(--color-btn-blue);

      &:hover {
        background-color: transparent;
        border: 2px solid var(--color-btn-blue);
        color: var(--color-btn-blue);
      }
      &:disabled {
        background-color: var(--color-btn-disabled-bg);
        color: var(--color-btn-disabled-text);
        border-color: transparent;
        cursor: not-allowed;
      }
    }

    /* green-aqua */
    &.green-aqua-btn {
      background-color: var(--color-btn-green-aqua);
      border: 2px solid var(--color-btn-green-aqua);

      &:hover {
        background-color: transparent;
        color: var(--color-btn-green-aqua);
        border: 2px solid var(--color-btn-green-aqua);
      }
      &:disabled {
        background-color: var(--color-btn-disabled-bg);
        color: var(--color-btn-disabled-text);
        border-color: transparent;
        cursor: not-allowed;
      }
    }

    /* light-green (success) */
    &.light-green-btn {
      background-color: var(--color-btn-light-green);
      border: 2px solid var(--color-btn-light-green);
      color: var(--color-white);

      &:hover {
        background-color: var(--color-btn-light-green-hover);
        border: 2px solid var(--color-btn-light-green-hover);
        color: var(--color-white);
      }

      &:disabled {
        background-color: var(--color-btn-disabled-bg);
        color: var(--color-btn-disabled-text);
        border-color: transparent;
        cursor: not-allowed;
      }
    }

    /* red */
    &.red-btn {
      background-color: var(--color-btn-red);
      border: 2px solid var(--color-btn-red);
      color: var(--color-white);

      &:hover {
        background-color: var(--color-btn-red-hover);
        border-color: var(--color-btn-red-hover);
      }
      &:disabled {
        background-color: var(--color-btn-disabled-bg);
        color: var(--color-btn-disabled-text);
        border-color: transparent;
        cursor: not-allowed;
      }
    }

    /* dark red */
    &.dark-red-btn {
      background-color: var(--color-btn-dark-red);
      border: 2px solid transparent;
      color: var(--color-white);

      &:hover {
        background-color: var(--color-btn-dark-red-hover);
      }
      &:disabled {
        background-color: var(--color-btn-disabled-bg);
        color: var(--color-btn-disabled-text);
        cursor: not-allowed;
      }
    }

    /* transparent */
    &.transparent-btn {
      background-color: transparent;
      color: var(--color-black);
      border: 2px solid transparent;

      &:hover {
        background-color: transparent;
        color: var(--color-black);
      }
      &:disabled {
        background-color: var(--color-btn-disabled-bg);
        color: var(--color-btn-disabled-text);
        border-color: transparent;
        cursor: not-allowed;
      }
    }

    &.menu-btn {
      color: var(--color-text-3);
      font-size: 15px;
      font-weight: 400;
      line-height: 21.429px;
      border-right: unset;
      border-left: unset;
      border-radius: unset;
    }
  }
</style>
